/**
 * O botão do SM, nas quatro variantes da origem.
 *
 * Papel: reproduz `.btn`, `.btn.primary`, `.btn.danger`, `.btn.ghost` e
 * `.btn.small` (l. 262–280) — `8px 14px`, raio de 6 px, Inter 12,5 px 600.
 *
 * Como usar: `variante` escolhe a cor e `pequeno` o tamanho de `.btn.small`,
 * usado no "Editar" da linha. O botão nasce com `type="button"`: dentro de um
 * formulário, um `submit` implícito faria Enter num campo gravar, e a origem só
 * grava no clique em "Salvar". O botão da toolbar é outro desenho, o
 * `BotaoDaBarra`. Desabilitado, ele não muda de texto nem de cor, como a spec
 * pede para "Salvar" e "Excluir" durante a gravação.
 */
import { type ComponentProps } from 'react';

import { cn } from '@/shared/lib/utils';

/** As quatro cores do `.btn` da origem. */
export type VarianteDoBotao = 'escuro' | 'primario' | 'perigo' | 'fantasma';

/** A cor, a borda e o hover de cada variante. */
const CLASSES_DA_VARIANTE: Readonly<Record<VarianteDoBotao, string>> = {
  escuro: 'border-0 bg-foreground text-superficie hover:bg-emb-grafite',
  primario: 'border border-brass-light bg-brass text-superficie hover:bg-brass-light',
  perigo:
    'border border-emb-perigo-borda bg-emb-perigo-fundo text-perigo hover:bg-emb-perigo-hover',
  fantasma: 'border border-emb-borda bg-background text-foreground hover:bg-emb-hover-bege',
};

interface BotaoProps extends ComponentProps<'button'> {
  /** A cor; `escuro` é o `.btn` sem modificador. */
  variante?: VarianteDoBotao;
  /** O tamanho de `.btn.small`. */
  pequeno?: boolean;
}

/**
 * Um botão do SM.
 *
 * @param variante - A cor da origem; `escuro` quando ausente.
 * @param pequeno - `true` para `5px 9px` e 11,5 px.
 * @param className - Classes de quem chama, aplicadas por último pelo `cn()`.
 * @param type - O `type` do botão; `button` quando ausente.
 * @returns O botão.
 */
export function Botao({
  variante = 'escuro',
  pequeno = false,
  className,
  type = 'button',
  ...props
}: BotaoProps) {
  return (
    <button
      type={type}
      className={cn(
        'cursor-pointer rounded-[6px] font-sans font-semibold disabled:cursor-default',
        pequeno ? 'px-[9px] py-[5px] text-[11.5px]' : 'px-3.5 py-2 text-[12.5px]',
        CLASSES_DA_VARIANTE[variante],
        className,
      )}
      {...props}
    />
  );
}
